import React, { useState, useEffect, useReducer, useRef, useContext } from "react";

import { isSameDay } from "date-fns";
import clsx from "clsx";
import { useTimezone } from "../../hooks/useTimezone";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  IconButton,
  makeStyles,
  useTheme,
} from "@material-ui/core";

import {
  AccessTime,
  Block,
  DoneAll,
  ExpandMore,
  GetApp,
  Reply,
  CheckCircleOutline,
  CheckCircle,
  Notifications,
  SwapHoriz,
  Note,
  Info,
  Person,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import LocationPreview from "../LocationPreview";
import VCardPreview from "../VCardPreview";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import { i18n } from "../../translate/i18n";

// Función para formatear el tamaño del archivo
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'kB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// ✅ FUNCIÓN HELPER: Normalizar URLs de media (aplicar misma solución que imágenes)
// Corrige doble /public/ y construye URLs absolutas correctamente
const normalizeMediaUrl = (mediaUrl) => {
  if (!mediaUrl) return "";
  
  const origin = window.location.origin.replace(/\/$/, "");
  let normalizedUrl = mediaUrl.trim();
  
  // ✅ Si ya es URL absoluta (http/https/blob), normalizar doble /public/
  if (normalizedUrl.startsWith("http://") || normalizedUrl.startsWith("https://") || normalizedUrl.startsWith("blob:")) {
    // ✅ CORRECCIÓN CRÍTICA: Eliminar TODAS las ocurrencias de doble /public/
    normalizedUrl = normalizedUrl.replace(/\/public\/\/public\//g, '/public/');
    normalizedUrl = normalizedUrl.replace(/\/public\/public\//g, '/public/');
    return normalizedUrl;
  }
  
  // ✅ Si empieza con /public/, construir URL completa
  if (normalizedUrl.startsWith("/public/")) {
    // ✅ CORRECCIÓN CRÍTICA: Eliminar doble /public/ ANTES de construir URL completa
    normalizedUrl = normalizedUrl.replace(/^\/public\/\/public\//, '/public/');
    normalizedUrl = normalizedUrl.replace(/^\/public\/public\//, '/public/');
    return `${origin}${normalizedUrl}`;
  }
  
  // ✅ Fallback: Si NO empieza con /public/, normalizar y construir URL
  let normalizedPath = normalizedUrl.replace(/^\/+/, "");
  normalizedPath = normalizedPath.replace(/^public\//, "");
  return `${origin}/public/${normalizedPath}`;
};

// ✅ Función para formatear nombre y número en formato "Nombre - +Número"
const formatSenderDisplay = (senderContact, contact, participant) => {
  // Prioridad 1: senderContact (del backend)
  const name = senderContact?.name || contact?.name || null;
  let number = senderContact?.number || contact?.number || 
               (participant ? participant.replace(/@lid|@s\.whatsapp\.net/g, '') : null);
  
  // Limpiar número (solo dígitos) para comparación
  const cleanNumber = number ? number.replace(/\D/g, '') : null;
  const cleanName = name ? name.replace(/\D/g, '') : null;
  
  // Si tenemos nombre Y número, mostrar "Nombre | +Número"
  if (name && number && cleanName !== cleanNumber && !name.match(/^\d+$/)) {
    // Formatear número con prefijo + (agregar solo si no lo tiene)
    const formattedNumber = number.startsWith('+') ? number : `+${number}`;
    return `${name} | ${formattedNumber}`;
  }
  
  // Si solo tenemos nombre (y no es un número), mostrar solo nombre
  if (name && cleanName !== cleanNumber && !name.match(/^\d+$/)) {
    return name;
  }
  
  // Si solo tenemos número, mostrar "+Número"
  if (number) {
    const formattedNumber = number.startsWith('+') ? number : `+${number}`;
    return formattedNumber;
  }
  
  // Fallback
  return 'Desconocido';
};

// ✅ Función para generar color único por remitente (similar a WhatsApp Web)
const getSenderColor = (identifier) => {
  // Paleta de colores de WhatsApp Web para remitentes en grupos
  const colors = [
    '#00a884', // Verde WhatsApp
    '#0da9e0', // Azul claro
    '#9c27b0', // Púrpura
    '#e91e63', // Rosa
    '#ff9800', // Naranja
    '#795548', // Marrón
    '#607d8b', // Gris azulado
    '#f44336', // Rojo
    '#4caf50', // Verde claro
    '#2196f3', // Azul
    '#ff5722', // Naranja oscuro
    '#009688', // Verde azulado
    '#673ab7', // Púrpura oscuro
  ];
  
  // Generar hash simple del identificador (número o ID)
  let hash = 0;
  const str = String(identifier || '');
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Usar el hash para seleccionar un color de la paleta
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
  },

  messagesList: {
    backgroundColor: theme.palette.messageBackground || (theme.mode === 'dark' ? "#111827" : "#f5f5f5"), // ✅ USA TEMA
    backgroundImage: "none",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.messageLeftBg || (theme.mode === 'dark' ? "#1e40af" : "#93c5fd"), // ✅ USA TEMA
    color: theme.palette.messageLeftText || (theme.mode === 'dark' ? "#ffffff" : "#000000"), // ✅ USA TEMA
    alignSelf: "flex-start",
    borderTopLeftRadius: "4px",
    borderTopRightRadius: "0px",
    borderBottomLeftRadius: "10px",
    borderBottomRightRadius: "10px",
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 8,
    paddingBottom: 4,
    boxShadow: theme.mode === 'dark' ? "0 1px 2px rgba(0, 0, 0, 0.5)" : "0 1px 1px #b3b3b3", // ✅ USA TEMA
    "&:hover $messageActionsButton": {
      opacity: 1,
      visibility: "visible",
    },
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.quotedLeftBg || (theme.mode === 'dark' ? "#374151" : "#f0f0f0"), // ✅ USA TEMA
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.quotedSideLeft || (theme.mode === 'dark' ? "#3b82f6" : "#6bcbef"), // ✅ USA TEMA
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.messageRightBg || (theme.mode === 'dark' ? "#059669" : "#6ee7b7"), // ✅ USA TEMA
    color: theme.palette.messageRightText || (theme.mode === 'dark' ? "#ffffff" : "#000000"), // ✅ USA TEMA
    alignSelf: "flex-end",
    borderTopLeftRadius: "0px",
    borderTopRightRadius: "4px",
    borderBottomLeftRadius: "10px",
    borderBottomRightRadius: "10px",
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 8,
    paddingBottom: 4,
    boxShadow: theme.mode === 'dark' ? "0 1px 2px rgba(0, 0, 0, 0.5)" : "0 1px 1px #b3b3b3", // ✅ USA TEMA
    "&:hover $messageActionsButton": {
      opacity: 1,
      visibility: "visible",
    },
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.palette.quotedRightBg || (theme.mode === 'dark' ? "#065f46" : "#cfe9ba"), // ✅ USA TEMA
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: theme.palette.quotedSideRight || (theme.mode === 'dark' ? "#10b981" : "#35cd96"), // ✅ USA TEMA
  },

  messageActionsButton: {
    display: "block", // ✅ CAMBIAR DE "none" A "block"
    position: "absolute", // ✅ CAMBIAR DE "relative" A "absolute"
    color: "#ffffff",
    zIndex: 10,
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    top: "4px",
    right: "4px",
    padding: "4px",
    fontSize: "14px",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { 
      backgroundColor: "rgba(0, 0, 0, 0.5)" 
    },
  },

  messageContactName: {
    display: "block",
    fontSize: "12.8px",
    fontWeight: 500,
    marginBottom: "4px",
    lineHeight: "22px",
    // El color se aplicará dinámicamente por remitente
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },
  
  textContentItemEdited: {
    overflowWrap: "break-word",
    padding: "3px 120px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: theme.palette.messageDeletedText || (theme.mode === 'dark' ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.36)"), // ✅ USA TEMA
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },
  forwardMessage: {
    fontSize: 12,
    fontStyle: "italic",
    position: "absolute",
    top: 0,
    left: 5,
    color: theme.palette.forwardMessageColor || (theme.mode === 'dark' ? "#9ca3af" : "#999"), // ✅ USA TEMA
    display: "flex",
    alignItems: "center"
  },
  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.mode === 'dark' ? "#ffffff" : "#ffffff", // ✅ USA TEMA - Blanco funciona en ambos modos con los nuevos fondos
    display: "flex",
    alignItems: "center",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: theme.palette.dailyTimestampBg || (theme.mode === 'dark' ? "#1f2937" : "#e1f3fb"), // ✅ USA TEMA
    margin: "10px",
    borderRadius: "10px",
    boxShadow: theme.mode === 'dark' ? "0 1px 2px rgba(0, 0, 0, 0.5)" : "0 1px 1px #b3b3b3", // ✅ USA TEMA
  },

  dailyTimestampText: {
    color: theme.palette.dailyTimestampText || (theme.mode === 'dark' ? "#9ca3af" : "#808888"), // ✅ USA TEMA
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({ ticket, ticketId, isGroup }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { formatTime, formatDateTime, parseNaiveDate } = useTimezone(); // ✅ Usar hook de timezone

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);
  const isMounted = useRef(true);
  const socketManager = useContext(SocketContext);
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const { showSelectMessageCheckbox } = useContext(ForwardMessageContext);

  // ✅ CORRECCIÓN: Extraer ticketId del ticket para evitar expresión compleja en dependencias
  const ticketIdFromTicket = ticket?.id;
  
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    // ✅ CORRECCIÓN: Actualizar ref con ticketId o ticket.id (priorizar ticketId prop)
    const effectiveTicketId = ticketId || ticketIdFromTicket;
    currentTicketId.current = effectiveTicketId;
    
    // console.log("[MessagesList] TicketId actualizado:", {
    //   ticketId,
    //   ticketIdFromTicket,
    //   effectiveTicketId,
    //   currentTicketIdRef: currentTicketId.current
    // });
    
    // ✅ REMOVIDO: El scroll se hará después de cargar los mensajes
  }, [ticketId, ticketIdFromTicket]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === undefined) return;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          // ✅ MEJORADO: Scroll al final solo cuando se carga la primera página
          if (pageNumber === 1) {
            // ✅ DELAY MÁS LARGO para asegurar que el DOM esté completamente renderizado
            setTimeout(() => {
              scrollToBottom();
            }, 300);
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  // ✅ AGREGADO: Scroll al final cuando se actualicen los mensajes
  useEffect(() => {
    if (messagesList.length > 0 && pageNumber === 1) {
      // ✅ DELAY para asegurar que el DOM esté renderizado
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [messagesList.length, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    // ✅ CORRECCIÓN: Función para suscribirse al canal del ticket
    const subscribeToTicket = () => {
      // ✅ Usar ticketId prop O ticket.id como fallback
      const effectiveTicketId = ticketId || ticket?.id || currentTicketId.current;
      if (effectiveTicketId) {
        // console.log("[MessagesList] Suscribiéndose al ticket:", effectiveTicketId);
        socket.emit("joinChatBox", `${effectiveTicketId}`);
      }
    };

    // ✅ CORRECCIÓN: Suscribirse inmediatamente si el ticketId está disponible
      subscribeToTicket();

    // ✅ FIX 05/12/2025: Guardar referencias a los listeners para poder limpiarlos correctamente
    const handleReady = () => {
      subscribeToTicket();
    };
    
    const handleConnect = () => {
      // Re-suscribirse después de una reconexión
      subscribeToTicket();
    };

    // ✅ CORRECCIÓN: Suscribirse cuando el socket está ready
    socket.on("ready", handleReady);

    // ✅ CORRECCIÓN: Re-suscribirse cuando el socket se reconecta
    socket.on("connect", handleConnect);

    socket.on(`company-${companyId}-appMessage`, (data) => {
      // ✅ DEBUG: Log removido para evitar spam en consola
      // console.log("🔔 [MessagesList] appMessage recibido:", {
      //   action: data.action,
      //   messageId: data.message?.id,
      //   messageTicketId: data.message?.ticketId,
      //   ticketId: data.ticket?.id,
      //   currentTicketId: currentTicketId.current,
      //   ticketIdFromProp: ticket?.id,
      //   hasMessage: !!data.message,
      //   hasTicket: !!data.ticket
      // });

      if (!isMounted.current) {
        // console.warn("[MessagesList] Componente desmontado, ignorando mensaje");
        return;
      }
      
      // ✅ CORRECCIÓN: Validar ticketId desde data.ticket O data.message
      const messageTicketId = data.message?.ticketId || data.ticket?.id;
      
      // ✅ CORRECCIÓN: Validar que el mensaje tenga propiedades mínimas necesarias
      if (!data.message || !data.message.id) {
        console.warn("[MessagesList] Mensaje recibido sin ID, ignorando:", data);
        return;
      }
      
      // ✅ CORRECCIÓN CRÍTICA: Verificar ticketId desde múltiples fuentes para evitar problemas de timing
      // Usar currentTicketId.current (ref actualizado) O ticket?.id (prop actual) como fallback
      const currentTicketIdValue = currentTicketId.current || ticket?.id;
      
      // ✅ DEBUG: Log removido para evitar spam en consola
      // console.log("🔍 [MessagesList] Comparando ticketIds:", {
      //   messageTicketId,
      //   currentTicketIdRef: currentTicketId.current,
      //   ticketIdFromProp: ticket?.id,
      //   currentTicketIdValue,
      //   match: messageTicketId === currentTicketIdValue
      // });

      // ✅ CORRECCIÓN: Comparar con el valor actualizado del ticketId (ref o prop)
      if (messageTicketId && currentTicketIdValue && messageTicketId === currentTicketIdValue) {
        // ✅ DEBUG: Log removido para evitar spam en consola
        // console.log("✅ [MessagesList] TicketId coincide, procesando mensaje");
        if (data.action === "update" && data.message) {
          // CORRECCION TIMEZONE: Usar parseNaiveDate para parsear correctamente las fechas del backend
          const parsedCreatedAt = data.message.createdAt ? parseNaiveDate(data.message.createdAt) : null;
          const createdAtValue = parsedCreatedAt && parsedCreatedAt.isValid() 
            ? parsedCreatedAt.format('YYYY-MM-DD HH:mm:ss') 
            : (data.message.createdAt || null);
          
          const messageToUpdate = {
            ...data.message,
            ticketId: data.message.ticketId || messageTicketId,
            mediaType: data.message.mediaType || "conversation",
            channel: data.message.channel || "whatsapp",
            fromMe: data.message.fromMe !== undefined ? data.message.fromMe : true,
            createdAt: createdAtValue
          };
          
          dispatch({
            type: "UPDATE_MESSAGE",
            payload: messageToUpdate,
          });
        } else if (data.action === "create" && data.message) {
          // ✅ CORRECCIÓN TIMEZONE: Asegurar que createdAt sea string naive antes de parsear
          // El backend ya envía createdAt como string naive vía formatMessageForSocket
          // Pero por seguridad, convertir cualquier formato a string antes de parsear
          let createdAtString = null;
          if (data.message.createdAt) {
            if (data.message.createdAt instanceof Date) {
              // Si es Date, convertir a ISO y luego parsear como naive
              createdAtString = data.message.createdAt.toISOString().replace('T', ' ').substring(0, 19);
            } else if (typeof data.message.createdAt === 'string') {
              // Si es string, usar directamente (ya viene formateado del backend)
              createdAtString = data.message.createdAt;
            }
          }
          
          // CORRECCION TIMEZONE: Usar parseNaiveDate para parsear correctamente las fechas del backend
          const parsedCreatedAt = createdAtString ? parseNaiveDate(createdAtString) : null;
          const createdAtValue = parsedCreatedAt && parsedCreatedAt.isValid() 
            ? parsedCreatedAt.format('YYYY-MM-DD HH:mm:ss') 
            : (createdAtString || null);
          
          const messageToAdd = {
            ...data.message,
            ticketId: data.message.ticketId || messageTicketId,
            mediaType: data.message.mediaType || "conversation",
            channel: data.message.channel || "whatsapp",
            fromMe: data.message.fromMe !== undefined ? data.message.fromMe : true,
            createdAt: createdAtValue
          };
          
          dispatch({
            type: "ADD_MESSAGE",
            payload: messageToAdd,
          });
          // ✅ Scroll automático al recibir nuevo mensaje
          setTimeout(() => scrollToBottom(), 100);
        }
      }
    });

    socket.on(`company-${companyId}-appMessage-update`, (data) => {
      if (!isMounted.current) {
        return;
      }
      
      // ✅ CORRECCIÓN: Verificar ticketId desde múltiples fuentes
      const currentTicketIdValue = currentTicketId.current || ticket?.id || ticketId;
      if (data.message?.ticketId === currentTicketIdValue) {
        dispatch({
          type: "UPDATE_MESSAGE",
          payload: data.message,
        });
      }
    });

    // ✅ CORRECCIÓN: Crear listener del canal del ticket solo si hay ticketId disponible
    // Usar una variable para almacenar el ticketId del canal y crear el listener condicionalmente
    const effectiveTicketIdForChannel = ticketId || ticket?.id;
    let ticketChannelListener = null;
    
    if (effectiveTicketIdForChannel) {
      ticketChannelListener = (data) => {
      if (!isMounted.current) {
        return;
      }
      
        // ✅ CORRECCIÓN: Procesar tanto create como update
      const messageTicketId = data.message?.ticketId || data.ticket?.id;
      
        // ✅ CORRECCIÓN: Validar que el mensaje tenga propiedades mínimas necesarias
      if (!data.message || !data.message.id) {
        console.warn("[MessagesList] Mensaje recibido sin ID en canal ticket, ignorando:", data);
        return;
      }
      
        // ✅ CORRECCIÓN: Verificar ticketId desde múltiples fuentes
        const currentTicketIdValue = currentTicketId.current || ticket?.id || ticketId;
        if (messageTicketId && currentTicketIdValue && messageTicketId === currentTicketIdValue) {
        if (data.action === "update" && data.message) {
          // CORRECCION TIMEZONE: Usar parseNaiveDate para parsear correctamente las fechas del backend
          const parsedCreatedAt = data.message.createdAt ? parseNaiveDate(data.message.createdAt) : null;
          const createdAtValue = parsedCreatedAt && parsedCreatedAt.isValid() 
            ? parsedCreatedAt.format('YYYY-MM-DD HH:mm:ss') 
            : (data.message.createdAt || null);
          
          const messageToUpdate = {
            ...data.message,
            ticketId: data.message.ticketId || messageTicketId,
            mediaType: data.message.mediaType || "conversation",
            channel: data.message.channel || "whatsapp",
            fromMe: data.message.fromMe !== undefined ? data.message.fromMe : true,
            createdAt: createdAtValue
          };
          
          dispatch({
            type: "UPDATE_MESSAGE",
            payload: messageToUpdate,
          });
        } else if (data.action === "create" && data.message) {
          // ✅ CORRECCIÓN TIMEZONE: Asegurar que createdAt sea string naive antes de parsear
          // El backend ya envía createdAt como string naive vía formatMessageForSocket
          // Pero por seguridad, convertir cualquier formato a string antes de parsear
          let createdAtString = null;
          if (data.message.createdAt) {
            if (data.message.createdAt instanceof Date) {
              // Si es Date, convertir a ISO y luego parsear como naive
              createdAtString = data.message.createdAt.toISOString().replace('T', ' ').substring(0, 19);
            } else if (typeof data.message.createdAt === 'string') {
              // Si es string, usar directamente (ya viene formateado del backend)
              createdAtString = data.message.createdAt;
            }
          }
          
          // CORRECCION TIMEZONE: Usar parseNaiveDate para parsear correctamente las fechas del backend
          const parsedCreatedAt = createdAtString ? parseNaiveDate(createdAtString) : null;
          const createdAtValue = parsedCreatedAt && parsedCreatedAt.isValid() 
            ? parsedCreatedAt.format('YYYY-MM-DD HH:mm:ss') 
            : (createdAtString || null);
          
          const messageToAdd = {
            ...data.message,
            ticketId: data.message.ticketId || messageTicketId,
            mediaType: data.message.mediaType || "conversation",
            channel: data.message.channel || "whatsapp",
            fromMe: data.message.fromMe !== undefined ? data.message.fromMe : true,
            createdAt: createdAtValue
          };
          
          dispatch({
            type: "ADD_MESSAGE",
            payload: messageToAdd,
          });
          // ✅ Scroll automático al recibir nuevo mensaje
          setTimeout(() => scrollToBottom(), 100);
        }
      }
      };
      
      socket.on(`${effectiveTicketIdForChannel}`, ticketChannelListener);
    }

    return () => {
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-appMessage`);
        socket.off(`company-${companyId}-appMessage-update`);
        socket.off(`company-${companyId}-contact`);
        // ✅ CORRECCIÓN: Limpiar listener del ticket usando el mismo ticketId usado para crear el listener
        if (effectiveTicketIdForChannel && ticketChannelListener) {
          socket.off(`${effectiveTicketIdForChannel}`, ticketChannelListener);
        } else {
          // Fallback: limpiar usando ticketId disponible
          const effectiveTicketId = ticketId || ticket?.id || currentTicketId.current;
          if (effectiveTicketId) {
            socket.off(`${effectiveTicketId}`);
          }
        }
        // ✅ FIX 05/12/2025: Limpiar listeners con referencias específicas para evitar memory leaks
        socket.off("ready", handleReady);
        socket.off("connect", handleConnect);
        socket.off("disconnect");
      }
    };
    // ✅ FIX 05/12/2025: Cambiar dependencia de 'ticket' (objeto) a 'ticketIdFromTicket' (primitivo)
    // para evitar re-ejecuciones innecesarias cuando el ticket se actualiza (nuevo mensaje, cambio de estado, etc.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, ticketIdFromTicket, socketManager]);

  // ✅ FIX 05/12/2025: UN SOLO useEffect controla isMounted cuando el componente se monta/desmonta
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []); // ✅ Sin dependencias = solo al montar/desmontar el componente

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    // ✅ MEJORADO: Scroll más robusto al final de los mensajes
    const messagesContainer = document.getElementById("messagesList");
    if (!messagesContainer) return;
    
    // ✅ MÉTODO 1: Usar scrollIntoView si hay ref
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ 
        behavior: "smooth", 
        block: "end",
        inline: "nearest"
      });
    } else {
      // ✅ MÉTODO 2: Scroll directo al final del contenedor
      messagesContainer.scrollTo({
        top: messagesContainer.scrollHeight,
        behavior: "smooth"
      });
    }
    
    // ✅ MÉTODO 3: Fallback adicional para asegurar scroll al final
    setTimeout(() => {
      if (messagesContainer.scrollTop < messagesContainer.scrollHeight - messagesContainer.clientHeight - 50) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 200);
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const hanldeReplyMessage = (e, message) => {
    //if (ticket.status === "open" || ticket.status === "group") {
    setAnchorEl(null);
    setReplyingMessage(message);
    //}
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else
    if (message.mediaType === "contactMessage") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      //console.log(array);
      //console.log(contact);
      //console.log(obj[0].number);
      return <VCardPreview contact={contact} numbers={obj[0].number} />
    }
    /* else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0].number} />
    } */
    /*else if (message.mediaType === "multi_vcard") {
      // console.log("multi_vcard")
      // console.log(message)
      
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map((v, idx) => (
              <VcardPreview key={`vcard-${message.id}-${idx}`} contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if (message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} message={message} />;
    } else if (message.mediaType === "audio") {
      // ✅ CORRECCIÓN: Normalizar URL de audio (misma solución que imágenes)
      const normalizedAudioUrl = normalizeMediaUrl(message.mediaUrl);
      return (
        <audio controls>
          <source src={normalizedAudioUrl} type="audio/mp3"></source>
        </audio>
      );
    } else if (message.mediaType === "video") {
      // ✅ CORRECCIÓN: Normalizar URL de video (misma solución que imágenes)
      const normalizedVideoUrl = normalizeMediaUrl(message.mediaUrl);
      return (
        <video
          className={classes.messageMedia}
          src={normalizedVideoUrl}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa', // ✅ USA TEMA
              borderRadius: '8px',
              border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`, // ✅ USA TEMA
              marginBottom: '8px',
              maxWidth: '300px'
            }}>
              {/* Icono del archivo */}
              {(() => {
                // ✅ USAR NOMBRE ORIGINAL DEL ARCHIVO PARA EL ICONO
                let fileName = message.body || 'document';
                
                // Si el body contiene el nombre del archivo, usarlo
                if (message.body && message.body.includes('.')) {
                  fileName = message.body;
                } else if (message.mediaUrl) {
                  // Extraer nombre desde la URL si no hay body
                  const urlFileName = message.mediaUrl.split('/').pop();
                  if (urlFileName && urlFileName.includes('.')) {
                    const parts = urlFileName.split('.');
                    if (parts.length > 2 && /^\d+$/.test(parts[0])) {
                      // Si la primera parte es un número (timestamp), usar el resto
                      fileName = parts.slice(1).join('.');
                    } else {
                      fileName = urlFileName;
                    }
                  }
                }
                
                const extension = fileName.split('.').pop()?.toLowerCase();
                
                // Colores e iconos según el tipo de archivo
                let iconConfig;
                if (['doc', 'docx'].includes(extension)) {
                  iconConfig = { bg: '#2b579a', text: 'W' }; // Word - Azul
                } else if (['xls', 'xlsx', 'csv'].includes(extension)) {
                  iconConfig = { bg: '#217346', text: '📊' }; // Excel/CSV - Verde
                } else if (['pdf'].includes(extension)) {
                  iconConfig = { bg: '#dc3545', text: 'PDF' }; // PDF - Rojo
                } else if (['ppt', 'pptx'].includes(extension)) {
                  iconConfig = { bg: '#d24726', text: 'P' }; // PowerPoint - Naranja
                } else if (['txt'].includes(extension)) {
                  iconConfig = { bg: '#6c757d', text: 'T' }; // Texto - Gris
                } else {
                  iconConfig = { bg: '#6c757d', text: '📄' }; // Default - Gris
                }
                
                return (
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: 'white',
                    backgroundColor: iconConfig.bg
                  }}>
                    {iconConfig.text}
                  </div>
                );
              })()}
              
              {/* Información del archivo */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.mode === 'dark' ? '#f9fafb' : '#212529', // ✅ USA TEMA
                  marginBottom: '2px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {(() => {
                    // ✅ USAR NOMBRE ORIGINAL DEL ARCHIVO
                    let fileName = message.body || 'Documento';
                    
                    // Si el body contiene el nombre del archivo, usarlo
                    if (message.body && message.body.includes('.')) {
                      fileName = message.body;
                    } else if (message.mediaUrl) {
                      // Extraer nombre desde la URL si no hay body
                      const urlFileName = message.mediaUrl.split('/').pop();
                      if (urlFileName && urlFileName.includes('.')) {
                        const parts = urlFileName.split('.');
                        if (parts.length > 2 && /^\d+$/.test(parts[0])) {
                          // Si la primera parte es un número (timestamp), usar el resto
                          fileName = parts.slice(1).join('.');
                        } else {
                          fileName = urlFileName;
                        }
                      }
                    }
                    
                    return fileName;
                  })()}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: theme.mode === 'dark' ? '#9ca3af' : '#6c757d', // ✅ USA TEMA
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {(() => {
                    // ✅ USAR TAMAÑO REAL DEL ARCHIVO
                    let fileName = message.body || 'document';
                    
                    // Si el body contiene el nombre del archivo, usarlo
                    if (message.body && message.body.includes('.')) {
                      fileName = message.body;
                    } else if (message.mediaUrl) {
                      // Extraer nombre desde la URL si no hay body
                      const urlFileName = message.mediaUrl.split('/').pop();
                      if (urlFileName && urlFileName.includes('.')) {
                        const parts = urlFileName.split('.');
                        if (parts.length > 2 && /^\d+$/.test(parts[0])) {
                          // Si la primera parte es un número (timestamp), usar el resto
                          fileName = parts.slice(1).join('.');
                        } else {
                          fileName = urlFileName;
                        }
                      }
                    }
                    
                    const extension = fileName.split('.').pop()?.toUpperCase() || 'DOC';
                    const fileSize = message.mediaSize || 0;
                    
                    return `${extension} • ${formatFileSize(fileSize)}`;
                  })()}
                </div>
              </div>
              
              {/* Botón de descarga minimalista */}
              <Button
                size="small"
                style={{
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa', // ✅ USA TEMA
                  border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#dee2e6'}`, // ✅ USA TEMA
                  color: theme.mode === 'dark' ? '#9ca3af' : '#6c757d', // ✅ USA TEMA
                  padding: 0
                }}
                target="_blank"
                href={normalizeMediaUrl(message.mediaUrl)}
              >
                <GetApp style={{ fontSize: '16px' }} />
              </Button>
            </div>
          </div>
        </>
      );
    }
};

  /*
    const renderMessageAck = (message) => {
      if (message.ack === 1) {
        return <AccessTime fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 2) {
        return <Done fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 3) {
        return <DoneAll fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 4 || message.ack === 5) {
        return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
      }
    };
    */

    const renderMessageAck = (message) => {
      // ✅ NO MOSTRAR ICONO DE RELOJ PARA MENSAJES DEL CONTACTO
      if (!message.fromMe) {
        return null;
      }
      
      if (message.ack === 0) {
        return <AccessTime fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 1) {
        return <CheckCircleOutline fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 2) {
        return <CheckCircle fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 3) {
        return <DoneAll fontSize="small" className={classes.ackIcons} />;
      }
      if (message.ack === 4) {
        return <DoneAll fontSize="small" className={classes.ackIcons} color="primary" />;
      }
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    };

  const renderDailyTimestamps = (message, index) => {
    // ✅ CORRECCIÓN TIMEZONE: Usar parseNaiveDate del hook useTimezone para parsear correctamente
    if (index === 0) {
      const parsedDate = message.createdAt ? parseNaiveDate(message.createdAt) : null;
      const isValidDate = parsedDate && parsedDate.isValid();
      
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {isValidDate 
              ? parsedDate.format("DD/MM/YYYY")
              : "Fecha no disponible"}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      const messageDay = message.createdAt 
        ? parseNaiveDate(message.createdAt) 
        : null;
      const previousMessageDay = messagesList[index - 1].createdAt
        ? parseNaiveDate(messagesList[index - 1].createdAt)
        : null;

      // ✅ VALIDACIÓN: Verificar que ambas fechas sean válidas antes de comparar y formatear
      const isValidMessageDay = messageDay && messageDay.isValid();
      const isValidPreviousDay = previousMessageDay && previousMessageDay.isValid();

      if (isValidMessageDay && isValidPreviousDay && !isSameDay(messageDay.toDate(), previousMessageDay.toDate())) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {messageDay.format("DD/MM/YYYY")}
            </div>
          </span>
        );
      }
    }
    // ✅ REMOVIDO: El lastMessageRef se aplicará directamente al último mensaje
  };

  const renderNumberTicket = (message, index, filteredMessagesArray) => {
    // ✅ CORREGIDO: Solo mostrar si hay mensaje anterior y el ticketId cambió
    if (index > 0 && index < filteredMessagesArray.length) {
      let messageTicket = message.ticketId;
      let previousMessageTicket = filteredMessagesArray[index - 1].ticketId;

      // ✅ SOLO MOSTRAR SI EL TICKETID REALMENTE CAMBIÓ Y NO ES EL PRIMER MENSAJE
      if (messageTicket && previousMessageTicket && messageTicket !== previousMessageTicket) {
        return (
          <center key={`ticket-change-${messageTicket}-${previousMessageTicket}`}>
            <div className={classes.ticketNunberClosed}>
              Conversa encerrada: {filteredMessagesArray[index - 1].createdAt && !isNaN(new Date(filteredMessagesArray[index - 1].createdAt).getTime()) 
                ? formatDateTime(filteredMessagesArray[index - 1].createdAt)
                : "Fecha no disponible"}
            </div>

            <div className={classes.ticketNunberOpen}>
              Conversa iniciada: {message.createdAt && !isNaN(new Date(message.createdAt).getTime()) 
                ? formatDateTime(message.createdAt)
                : "Fecha no disponible"}
            </div>
          </center>
        );
      }
    }
    return null; // ✅ RETORNAR null SI NO HAY CAMBIO DE TICKET
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span 
              className={classes.messageContactName}
              style={{ 
                color: getSenderColor(
                  message.quotedMsg?.senderContact?.number || 
                  message.quotedMsg?.senderContact?.id || 
                  message.quotedMsg?.contact?.number ||
                  message.quotedMsg?.participant
                )
              }}
            >
              {formatSenderDisplay(message.quotedMsg?.senderContact, message.quotedMsg?.contact, message.quotedMsg?.participant)}
            </span>
          )}

          {message.quotedMsg.mediaType === "audio"
            && (
              <div className={classes.downloadMedia}>
                <audio controls>
                  <source src={normalizeMediaUrl(message.quotedMsg.mediaUrl)} type="audio/mp3"></source>
                </audio>
              </div>
            )
          }
          {message.quotedMsg.mediaType === "video"
            && (
              <video
                className={classes.messageMedia}
                src={normalizeMediaUrl(message.quotedMsg.mediaUrl)}
                controls
              />
            )
          }
          {message.quotedMsg.mediaType === "application"
            && (
              <div className={classes.downloadMedia}>
                <Button
                  startIcon={<GetApp />}
                  color="primary"
                  variant="outlined"
                  target="_blank"
                  href={normalizeMediaUrl(message.quotedMsg.mediaUrl)}
                >
                  Download
                </Button>
              </div>
            )
          }

          {message.quotedMsg.mediaType === "image" ? (
            <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />
          ) : (
            message.quotedMsg?.body
          )}

        </div>
      </div>
    );
  };

  // ✅ FUNCIÓN PARA RENDERIZAR REACCIONES SOBRE EL MENSAJE (SOLO LA ÚLTIMA REACCIÓN)
  const renderMessageReactions = (message) => {
    if (!message.reactions || message.reactions.length === 0) {
      return null;
    }

    // ✅ MOSTRAR SOLO LA ÚLTIMA REACCIÓN (COMO WHATSAPP WEB)
    const lastReaction = message.reactions[message.reactions.length - 1];

    return (
      <div style={{
        position: 'absolute',
        bottom: '-30px', // ✅ POSICIÓN MÁS BAJA PARA NO OCULTAR TIMESTAMP
        right: '8px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '16px',
        padding: '4px 8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
        zIndex: 5,
        fontSize: '16px',
        minWidth: '32px',
        justifyContent: 'center'
      }}>
        <span>{lastReaction.type}</span>
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      // ✅ FILTRAR MENSAJES DE REACCIÓN Y MENSAJES VACÍOS
      const filteredMessages = messagesList.filter(message => {
        // ✅ EXCLUIR MENSAJES DE REACCIÓN
        if (message.mediaType === "reactionMessage") {
          return false;
        }
        // ✅ PERMITIR MENSAJES INTERNOS, ALERTAS DE SISTEMA Y LLAMADAS (siempre se muestran)
        if (message.mediaType?.startsWith("internal_") || 
            message.mediaType === "system-alert" || 
            message.mediaType === "call_log") {
          return true;
        }
        // ✅ EXCLUIR MENSAJES VACÍOS (sin body y sin mediaUrl)
        if (!message.body && !message.mediaUrl) {
          return false;
        }
        return true;
      });
      
      const viewMessagesList = filteredMessages.map((message, index) => {

        // ✅ NUEVO (31/12/2025): RENDERIZAR MENSAJES DE ALERTA DE SISTEMA (system-alert)
        if (message.mediaType === "system-alert") {
          // Dividir el mensaje en líneas para formatearlo
          const alertLines = message.body?.split('\n').filter(line => line.trim() !== '') || [];
          
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                margin: '12px 0',
                padding: '12px 0'
              }}>
                {/* ✅ LÍNEA DIVISORIA */}
                <div style={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
                  marginBottom: '12px'
                }} />
                
                {/* ✅ BANNER DE ALERTA AMARILLO/NARANJA */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  backgroundColor: theme.mode === 'dark' ? '#78350f' : '#fef3c7', // Amarillo/naranja oscuro en dark, claro en light
                  border: `1px solid ${theme.mode === 'dark' ? '#f59e0b' : '#fbbf24'}`,
                  borderRadius: '8px',
                  padding: '12px 20px',
                  maxWidth: '600px',
                  width: '100%',
                  color: theme.mode === 'dark' ? '#fbbf24' : '#92400e',
                  fontSize: '13px',
                  lineHeight: '1.6'
                }}>
                  {/* ✅ ICONO DE ALERTA */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '4px',
                    fontWeight: 600
                  }}>
                    <span role="img" aria-label="warning">⚠️</span>
                    {alertLines.length > 0 && (
                      <span>{alertLines[0]}</span>
                    )}
                  </div>
                  
                  {/* ✅ LÍNEAS ADICIONALES DEL MENSAJE */}
                  {alertLines.length > 1 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      width: '100%',
                      marginTop: '8px'
                    }}>
                      {alertLines.slice(1).map((line, idx) => (
                        <div key={idx} style={{
                          textAlign: 'left',
                          fontWeight: idx === alertLines.length - 2 ? 500 : 400, // Última línea (timestamp) más clara
                          opacity: idx === alertLines.length - 2 ? 0.8 : 1,
                          fontSize: idx === alertLines.length - 2 ? '12px' : '13px'
                        }}>
                          {line.trim()}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        }

        // ✅ RENDERIZAR MENSAJES INTERNOS (toast, transfer, note, action)
        if (message.mediaType?.startsWith("internal_")) {
          const internalType = message.mediaType.replace("internal_", "");
          let icon, className;
          
          switch (internalType) {
            case "toast":
              icon = <Notifications className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalToast);
              break;
            case "transfer":
              icon = <SwapHoriz className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalTransfer);
              break;
            case "note":
              icon = <Note className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalNote);
              break;
            case "action":
              icon = <Info className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalAction);
              break;
            case "signature":
              icon = <Person className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalAction);
              break;
            case "delete":
              icon = <Person className={classes.internalMessageIcon} />;
              className = clsx(classes.messageInternal, classes.messageInternalAction);
              break;
            default:
              icon = null;
              className = classes.messageInternal;
          }

          // ✅ DETECTAR SI ES MENSAJE DE TRANSFERENCIA O DEVOLUCIÓN PARA FORMATO ESPECIAL
          const isTransferMessage = internalType === "action" && (
            message.body?.includes("transfirió") || 
            message.body?.includes("devuelta") || 
            message.body?.includes("asignada") ||
            message.body?.includes("cambió de")
          );
          
          // ✅ DEBUG: Log temporal removido para evitar spam en consola
          // if (isTransferMessage && process.env.NODE_ENV === 'development') {
          //   console.log('🔍 Transfer message detected:', {
          //     body: message.body,
          //     bodyLength: message.body?.length,
          //     hasNewlines: message.body?.includes('\n'),
          //     lines: message.body?.split(/\r?\n/),
          //     mediaType: message.mediaType
          //   });
          // }

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              {isTransferMessage ? (
                // ✅ FORMATO ESPECIAL PARA TRANSFERENCIAS Y DEVOLUCIONES - UNA SOLA LÍNEA DIVISORIA
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  margin: '12px 0',
                  padding: '12px 0'
                }}>
                  {/* ✅ UNA SOLA LÍNEA DIVISORIA */}
                  <div style={{
                    width: '100%',
                    height: '1px',
                    backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
                    marginBottom: '12px'
                  }} />
                  
                  {/* ✅ CONTENIDO CENTRADO CON ICONO ENTRE LÍNEAS */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    color: theme.mode === 'dark' ? '#d1d5db' : '#666',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    padding: '0 20px',
                    position: 'relative'
                  }}>
                    {/* ✅ DIVIDIR EL TEXTO EN LÍNEAS Y COLOCAR ICONO ENTRE ELLAS */}
                    {(() => {
                      // Limpiar el texto y dividir por saltos de línea
                      const cleanBody = message.body?.trim() || '';
                      const lines = cleanBody.split(/\r?\n/).filter(line => line.trim() !== '');
                      
                      // ✅ DEBUG: Log removido para evitar spam en consola
                      // if (process.env.NODE_ENV === 'development') {
                      //   console.log('🔍 Transfer message lines:', lines.length, lines);
                      // }
                      
                      // Si tiene 3 o más líneas, usar formato estructurado
                      if (lines.length >= 3) {
                        return (
                          <>
                            {/* Línea 1: "Se transfirió la conversación de usuarios" */}
                            <div style={{ 
                              marginBottom: '4px',
                              fontWeight: 400
                            }}>
                              {lines[0].trim()}
                            </div>
                            
                            {/* Línea 2: Icono y nombre del usuario en la misma línea */}
                            <div style={{ 
                              marginBottom: '4px',
                              fontWeight: 400,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}>
                              {icon && (
                                <span style={{ 
                                  opacity: 0.8,
                                  color: theme.mode === 'dark' ? '#9ca3af' : '#666',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  {icon}
                                </span>
                              )}
                              <span>{lines[1].trim()}</span>
                            </div>
                            
                            {/* Línea 3: Fecha y hora */}
                            <div style={{
                              fontWeight: 400,
                              fontSize: '12px',
                              opacity: 0.8
                            }}>
                              {lines[2].trim()}
                            </div>
                          </>
                        );
                      } else if (lines.length === 2) {
                        // Si tiene 2 líneas, mostrar con icono en medio
                        return (
                          <>
                            <div style={{ marginBottom: '4px' }}>
                              {lines[0].trim()}
                            </div>
                            {icon && (
                              <div style={{ 
                                margin: '6px 0',
                                opacity: 0.8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {icon}
                              </div>
                            )}
                            <div>
                              {lines[1].trim()}
                            </div>
                          </>
                        );
                      } else {
                        // Fallback: mostrar mensaje completo con icono arriba
                        return (
                          <>
                            {icon && (
                              <div style={{ 
                                marginBottom: '8px',
                                opacity: 0.8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {icon}
                              </div>
                            )}
                            <div style={{ whiteSpace: 'pre-line' }}>
                              {cleanBody}
                            </div>
                          </>
                        );
                      }
                    })()}
                  </div>
                </div>
              ) : (
                // ✅ FORMATO NORMAL PARA OTROS MENSAJES INTERNOS
                // ✅ NUEVO: Si es internal_signature o internal_delete, alinearlo a la derecha
                (internalType === "signature" || internalType === "delete") ? (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginRight: 20,
                    marginTop: 2,
                    marginBottom: 2
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: theme.mode === 'dark' ? '#9ca3af' : '#666',
                      fontSize: '13px',
                      padding: '4px 12px',
                      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f3f4f6',
                      borderRadius: '8px'
                    }}>
                      {icon}
                      <span>{message.body}</span>
                      <span style={{ fontSize: 11, opacity: 0.7 }}>
                        {formatTime(message.createdAt || new Date())}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className={className}>
                    {icon}
                    <span>{message.body}</span>
                    <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.7 }}>
                      {formatTime(message.createdAt || new Date())}
                    </span>
                  </div>
                )
              )}
            </React.Fragment>
          );
        }

        if (message.mediaType === "call_log") {
          // ✅ DETECTAR SI ES LLAMADA ACEPTADA O PERDIDA
          const isAcceptedCall = message.body && message.body.toLowerCase().includes("aceptada");
          const iconColor = isAcceptedCall ? "#4caf50" : "#df3333"; // Verde para aceptada, rojo para perdida
          
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageCenter}>
                {isGroup && (
                  <span 
                    className={classes.messageContactName}
                    style={{ 
                      color: getSenderColor(
                        message.senderContact?.number || 
                        message.senderContact?.id || 
                        message.contact?.number ||
                        message.participant
                      )
                    }}
                  >
                    {formatSenderDisplay(message.senderContact, message.contact, message.participant)}
                  </span>
                )}
                <div>
                  {isAcceptedCall ? (
                    // ✅ ICONO PARA LLAMADA ACEPTADA: Teléfono normal (verde) - sin X
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                      <path fill={iconColor} d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1z"/>
                    </svg>
                  ) : (
                    // ✅ ICONO PARA LLAMADA PERDIDA: Teléfono con X (rojo)
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                      <path fill={iconColor} d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                    </svg>
                  )}
                  <span>{message.body || (message.createdAt && !isNaN(new Date(message.createdAt).getTime()) ? `Llamada de voz/vídeo perdida a las ${formatTime(message.createdAt)}` : "Llamada de voz/vídeo perdida")}</span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              <div
                className={classes.messageLeft}
                title={message.queueId && message.queue?.name}
                onDoubleClick={(e) => hanldeReplyMessage(e, message)}
                style={{ position: 'relative' }}
                ref={index === filteredMessages.length - 1 ? lastMessageRef : null}
              >
                {showSelectMessageCheckbox && (
                  <SelectMessageCheckbox
                    // showSelectMessageCheckbox={showSelectMessageCheckbox}
                    message={message}
                  // selectedMessagesList={selectedMessagesList}
                  // setSelectedMessagesList={setSelectedMessagesList}
                  />
                )}
                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}
                    ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> Encaminhada
                    </span>
                    <br />
                  </div>
                )}
                {isGroup && !message.fromMe && (
                  <span 
                    className={classes.messageContactName}
                    style={{ 
                      color: getSenderColor(
                        message.senderContact?.number || 
                        message.senderContact?.id || 
                        message.contact?.number ||
                        message.participant
                      )
                    }}
                  >
                    {formatSenderDisplay(message.senderContact, message.contact, message.participant)}
                  </span>
                )}

                {/* aviso de mensagem apagado pelo contato */}
                {message.isDeleted && (
                  <div>
                    <span className={"message-deleted"}
                    >Essa mensagem foi apagada pelo contato &nbsp;
                      <Block
                        color="error"
                        fontSize="small"
                        className={classes.deletedIcon}
                      />
                    </span>
                  </div>
                )}

                {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "vcard" || message.mediaType === "contactMessage"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {message.mediaType !== "reactionMessage" && (
                    <MarkdownWrapper message={message}>
                      {message.mediaType === "locationMessage" || message.mediaType === "contactMessage" 
                        ? null
                        : message.body}
                    </MarkdownWrapper>
                  )}
                                  
                  <span className={classes.timestamp}>
                    {" "}{formatTime(message.createdAt || message.timestamp || new Date())}
                    {renderMessageAck(message)}
                  </span>
                  {/* ✅ BOTÓN DE OPCIONES FUERA DEL TIMESTAMP - PARTE SUPERIOR DERECHA */}
                  <IconButton
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                    style={{
                      position: 'absolute',
                      top: '0px',       // ✅ EXACTAMENTE AL TOPE DEL BORDE DEL CHAT BUBBLE
                      right: '8px',     // ✅ MARGEN DERECHO DEL CHAT BUBBLE
                      padding: "4px",
                      color: "#ffffff",
                      fontSize: "14px",
                      backgroundColor: "rgba(0, 0, 0, 0.3)",
                      borderRadius: "50%",
                      width: "24px",
                      height: "24px",
                      zIndex: 10,
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.5)"
                      }
                    }}
                  >
                    <ExpandMore style={{ fontSize: "14px" }} />
                  </IconButton>
                </div>
                {/* ✅ MOSTRAR REACCIONES SOBRE EL MENSAJE */}
                {renderMessageReactions(message)}
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index, filteredMessages)}
              {renderMessageDivider(message, index)}
              <div 
                className={classes.messageRight}
                onDoubleClick={(e) => hanldeReplyMessage(e, message)}
                style={{ position: 'relative' }}
                ref={index === filteredMessages.length - 1 ? lastMessageRef : null}
              >
              {showSelectMessageCheckbox && (
                <SelectMessageCheckbox
                  // showSelectMessageCheckbox={showSelectMessageCheckbox}
                  message={message}
                // selectedMessagesList={selectedMessagesList}
                // setSelectedMessagesList={setSelectedMessagesList}
                />
              )}
                {message.isForwarded && (
                  <div>
                    <span className={classes.forwardMessage}
                    ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> Encaminhada
                    </span>
                    <br />
                  </div>
                )}
                {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "vcard" || message.mediaType === "contactMessage"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {/* Mostrar el body para todos los tipos de mensaje excepto ubicaciones */}
                  {message.mediaType !== "locationMessage" && 
                   message.body && (
                    <MarkdownWrapper message={message}>{message.body}</MarkdownWrapper>
                  )}
                  
                
                  <span className={classes.timestamp}>
                    {" "}{formatTime(message.createdAt || message.timestamp || new Date())}
                    {renderMessageAck(message)}
                  </span>
                  {/* ✅ BOTÓN DE OPCIONES FUERA DEL TIMESTAMP - PARTE SUPERIOR DERECHA */}
                  <IconButton
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                    style={{
                      position: 'absolute',
                      top: '0px',       // ✅ EXACTAMENTE AL TOPE DEL BORDE DEL CHAT BUBBLE
                      right: '8px',     // ✅ MARGEN DERECHO DEL CHAT BUBBLE
                      padding: "4px",
                      color: "#ffffff",
                      fontSize: "14px",
                      backgroundColor: "rgba(0, 0, 0, 0.3)",
                      borderRadius: "50%",
                      width: "24px",
                      height: "24px",
                      zIndex: 10,
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.5)"
                      }
                    }}
                  >
                    <ExpandMore style={{ fontSize: "14px" }} />
                  </IconButton>
                </div>
                {/* ✅ MOSTRAR REACCIONES SOBRE EL MENSAJE */}
                {renderMessageReactions(message)}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>{i18n.t("hardcodedElements.sayHelloToNewContact")}</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;